import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { Vega } from "react-vega";
import { ResizableBox } from "react-resizable";
import debounce from "lodash/debounce";

import { QuestionWrapper } from "../Components/QuestionWrapper";
import { MainTitle } from "../Components/MainTitle";
import { Footer } from "../Components/Footer";
import { TotalAttempts } from "../Components/TotalAttempts";
import { SubmitButton } from "../Components/SubmitButton";
import { Question } from "../Components/Question";
import { ContentDropdown } from "@learningblocks/shared-components";

import { Option } from "./Option";
import { ResponseArea } from "./ResponseArea";
import {
  isBiggestPosOrNeg,
  isSmallestPosOrNeg,
  mostlyPositive,
  mostlyNegative,
  histRangeMatch,
  histBinWidthMatch,
} from "./Checks";

import {
  initPlotQuestion,
  selectOption,
  validateSubmission,
  updateTextAnswer,
} from "./store/actions";
import "./style.scss";

/**
 * Component for displaying a single-select question with a plot.
 */
const PlotSingleSelect = (props) => {
  // State for managing plot dimensions
  const [plotHeight, setPlotHeight] = useState(350);
  const [plotWidth, setPlotWidth] = useState(250);

  const titleText =
    props.plot === "customSpec" ? props.title : props.plots[props.plot]?.title;
  const yHeaderText =
    props.plot === "customSpec"
      ? props.headerY
      : props.plots[props.plot]?.headerY;
  const xHeaderText =
    props.plot === "customSpec"
      ? props.headerX
      : props.plots[props.plot]?.headerX;

  /**
   * Adjust plot dimensions dynamically based on parent element's size.
   */
  useEffect(() => {
    if (
      (props.plots[props.plot] || props.plot === "customSpec") &&
      props.questionData
    ) {
      const adjustPlotWidth = debounce(() => {
        const parentElement = document.getElementById(
          `plot_cscq_plotWrapper_${props.id}`
        )?.parentElement;
        if (parentElement) {
          setPlotWidth(parentElement.offsetWidth - 20); // Account for padding
        }
      }, 20);

      adjustPlotWidth();

      const plotWrapper = document.getElementById(
        `plot_cscq_plotWrapper_${props.id}`
      );
      const resizeObserver = new ResizeObserver(adjustPlotWidth);

      if (plotWrapper) resizeObserver.observe(plotWrapper);

      return () => {
        resizeObserver.disconnect();
        adjustPlotWidth.cancel(); // Cancel pending debounced calls
      };
    }
  }, [props.plots, props.plot, props.questionData, props.id]);

  /**
   * Lock the plot and initialize question state.
   */
  useEffect(() => {
    if (props.plots[props.plot]?.locked || props.plot === "customSpec") {
      // console.log(JSON.stringify(props.plots[props.plot].spec))
      initializePlotState();
    }
  }, [props.plot, props.plots]);

  /**
   * Initialize the plot state with appropriate answers and options.
   */
  const initializePlotState = () => {
    const answerId = [];
    const optionsCellData = [];
    const optionSpecificCellData = {};
    const options = props.options || [];

    // Process options and collect cell data
    options.forEach((option, index) => {
      if (option.refCell) {
        optionsCellData.push({
          index,
          value:
            props.plots[props.plot]?.cellsUsed.data[option.refCell.row][
              option.refCell.col
            ].m,
        });
      }
      if (option.refCells) {
        optionSpecificCellData[option.id] = option.refCells.map((refCell) => ({
          index,
          value:
            props.plots[props.plot]?.cellsUsed.data[refCell.row][refCell.col].m,
        }));
      }
    });

    // Determine the correct answer(s) based on check types
    options.forEach((option, index) => {
      switch (option.checkType) {
        case "BIGGEST_POS_OR_NEG":
          if (isBiggestPosOrNeg(optionsCellData, index))
            answerId.push(option.id);
          break;
        case "SMALLEST_POS_OR_NEG":
          if (isSmallestPosOrNeg(optionsCellData, index))
            answerId.push(option.id);
          break;
        case "MOSTLY_POSITIVE":
          if (mostlyPositive(optionSpecificCellData[option.id]))
            answerId.push(option.id);
          break;
        case "MOSTLY_NEGATIVE":
          if (mostlyNegative(optionSpecificCellData[option.id]))
            answerId.push(option.id);
          break;
        case "HIST_RANGE_MATCH":
          if (
            histRangeMatch(option.range, {
              min: props.plots[props.plot]?.histogramRangeMin,
              max: props.plots[props.plot]?.histogramRangeMax,
            })
          )
            answerId.push(option.id);
          break;
        case "HIST_BIN_WIDTH_MATCH":
          if (
            histBinWidthMatch(
              option.value,
              props.plots[props.plot]?.histogramBinWidth
            )
          )
            answerId.push(option.id);
          break;
        case "FALLBACK":
          if (answerId.length === 0) answerId.push(option.id);
          break;
        default:
          break;
      }
    });

    // Dispatch Redux action to initialize plot question
    props.initPlotQuestion({
      key: props.id,
      plot: props.plot,
      plotFallback: props.plotFallback,
      question: props.question,
      options,
      hintDetails: props.hintDetails,
      answerId,
      totalAttempts: props.totalAttempts,
      feedback: props.feedback || "",
      answerType: props.answerType,
    });
  };

  /**
   * Determine if the submit button should be disabled.
   */
  const setSubmitDisabled = () => {
    if (props.complete) return true;
    if (props.answerType === "OPEN_ENDED" && props.questionData.answer !== "")
      return false;
    return (props.questionData.options || []).every(
      (option) => option.disabled || !option.selected
    );
  };

  /**
   * Render the question options.
   */
  const renderOptions = (options) =>
    options.map((option, index) => (
      <Option
        key={index}
        {...option}
        index={index}
        tabIndex="0"
        selectOption={(idx) =>
          props.selectOption({ questionId: props.id, optionIndex: idx })
        }
        isCorrect={props.questionData.answerId.includes(option.id)}
      />
    ));

  /**
   * Render the main title based on the question type.
   */
  const renderTitle = (type) => {
    switch (type) {
      case "SINGLE_SELECT":
        return "Single Select Question";
      case "OPEN_ENDED":
        return "Open-Ended Question";
      default:
        return "Question";
    }
  };

  const inheritParams = () => {
    if (props.plot === "customSpec") {
      return props.customSpec.params;
    }

    const spec = props.plots[props.plot].spec;
    if (!Array.isArray(spec.params)) {
      return [];
    }

    // Create a new array with updated values
    const updatedParams = spec.params.map((param) => {
      const newValue = props.plots[props.plot][param.name];
      return {
        ...param, // Keep existing properties
        value: Number(newValue), // Update value from state
      };
    });

    return updatedParams;
  };

  return (
    <div>
      <QuestionWrapper className={`cscq ${props.className}`}>
        <MainTitle
          title={renderTitle(props.answerType)}
          className="cscq__mainTitle"
        />
        {(props.plots[props.plot] || props.plot === "customSpec") &&
        props.questionData ? (
          <>
            <Question
              tabIndex="0"
              text={props.question}
              className="cscq__question"
            />
            <div
              className="plotBounds"
              aria-label="Custom plot, description currently unavailable."
            >
              <ResizableBox
                height={plotHeight}
                minConstraints={[250, 250]}
                maxConstraints={[Infinity, 700]}
                onResize={(e, data) => setPlotHeight(data.size.height)}
                className="plot_cscq_plotWrapper"
                id={`plot_cscq_plotWrapper_${props.id}`}
                style={{
                  marginLeft: yHeaderText ? "20px" : null,
                }}
              >
                {titleText && (
                  <div className="plot_cscq_title_header">{titleText}</div>
                )}
                {yHeaderText && (
                  <div className="plot_cscq_y_header">{yHeaderText}</div>
                )}
                <div
                  style={{
                    width: plotWidth,
                    height: plotHeight - 30,
                    overflow: "hidden",
                  }}
                >
                  <Vega
                    spec={{
                      ...JSON.parse(
                        JSON.stringify(
                          props.plot === "customSpec"
                            ? props.customSpec
                            : props.plots[props.plot].spec
                        )
                      ), // Deep clone the spec
                      config: {
                        ...props.plots[props.plot]?.spec.config, // Preserve existing config options
                        ...props.customSpec?.config,
                        aria: true, // Enable ARIA attributes
                        description:
                          props.plot === "customSpec"
                            ? props.customAltText
                            : `A graph generated from the spreadsheet, see the cell range that you used to generate this graph. Sheet: ${props.plots[props.plot]?.sheetName}. Cell range: ${props.plots[props.plot]?.cellsUsed.range}.`, // // This sets aria-label
                      },
                      params: inheritParams(),
                    }}
                    actions={false} // Disable Vega's built-in action menu
                    width={plotWidth}
                    height={plotHeight - 30}
                  />
                </div>
                {xHeaderText && (
                  <div className="plot_cscq_x_header">{xHeaderText}</div>
                )}
              </ResizableBox>
            </div>
            {renderOptions(props.questionData.options)}
            {props.answerType === "OPEN_ENDED" && (
              <ResponseArea
                feedback={props.questionData.feedback}
                onValueChange={(value) =>
                  props.updateTextAnswer({ questionId: props.id, value })
                }
                disabled={props.questionData.disabled}
                renderFeedback={props.questionData.complete}
                answer={props.questionData.answer}
              />
            )}
            {props.hintDetails && (
              <ContentDropdown contentDetails={props.hintDetails} />
            )}
            <Footer className="cscq__footer">
              {props.answerType === "SINGLE_SELECT" && (
                <TotalAttempts
                  totalAttempts={props.questionData.totalAttempts}
                  className="cscq__footer__totalAttempts"
                />
              )}
              <SubmitButton
                disabled={setSubmitDisabled()}
                onSubmit={() => props.validateSubmission(props.id)}
                label="Submit"
              />
            </Footer>
          </>
        ) : (
          <div className="plot_cscq_fallback">
            <Question
              tabIndex="0"
              text={props.plotFallback}
              className="cscq__question"
            />
          </div>
        )}
      </QuestionWrapper>
    </div>
  );
};

/**
 * Map Redux state to component props.
 */
const mapStateToProps = (state, ownProps) => ({
  ...ownProps,
  plots: state.spreadsheet.spreadsheetData.plots,
  questionData: (state.plotCSCQ || {})[ownProps.id] || null,
});

/**
 * Map Redux dispatch actions to component props.
 */
const mapDispatchToProps = (dispatch) => ({
  initPlotQuestion: (data) => dispatch(initPlotQuestion(data)),
  selectOption: (data) => dispatch(selectOption(data)),
  validateSubmission: (data) => dispatch(validateSubmission(data)),
  updateTextAnswer: (data) => dispatch(updateTextAnswer(data)),
});

// Export the connected component
const ConnectedPlotSingleSelect = connect(
  mapStateToProps,
  mapDispatchToProps
)(PlotSingleSelect);
export { ConnectedPlotSingleSelect as PlotSingleSelect };
